import * as React from 'react';
import {type SelectValue} from './types';
import {type SelectOption} from '../../lib/selector-option';
import {cn} from '../../lib/cn';
import type {ReactNode} from 'react';

type AirbnbSelectDesktopMenuProps<
  T,
  V extends SelectValue,
  L extends string | number | ReactNode,
> = {
  id: string;
  options: SelectOption<T, V, L>[];
  labelledBy: string;
  describedBy?: string;
  selectedValue?: SelectOption<T, V, L> | null;
  highlightedIndex?: number;
  onOptionClick: (option: SelectOption<T, V, L>) => void;
  onOptionHover?: (index: number) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  disabled?: boolean;
  menuClassName?: string;
  listRef?: React.RefObject<HTMLDivElement>;
  selectedOptionRef?: (index: number, node: HTMLButtonElement | null) => void;
  getOptionId: (index: number) => string;
  noOptionsMessage?: () => string | undefined;
};

function AirbnbSelectDesktopMenu<
  T,
  V extends SelectValue,
  L extends string | number | ReactNode,
>({
  id,
  options,
  labelledBy,
  describedBy,
  selectedValue,
  highlightedIndex,
  onOptionClick,
  onOptionHover,
  onKeyDown,
  disabled,
  menuClassName,
  listRef,
  selectedOptionRef,
  getOptionId,
  noOptionsMessage,
}: AirbnbSelectDesktopMenuProps<T, V, L>) {
  const emptyMessage = noOptionsMessage?.();

  return (
    <div
      id={id}
      ref={listRef}
      role="listbox"
      tabIndex={-1}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      aria-activedescendant={
        highlightedIndex !== undefined && highlightedIndex >= 0
          ? getOptionId(highlightedIndex)
          : undefined
      }
      onKeyDown={onKeyDown}
      className={cn('max-h-[280px] overflow-y-auto p-2 outline-none', menuClassName)}
    >
      {options.length === 0 && emptyMessage ? (
        <div className="px-4 py-3 text-base leading-6 text-[var(--chekin-airbnb-gray-text)]">
          {emptyMessage}
        </div>
      ) : null}
      {options.map((option, index) => {
        const isSelected = selectedValue?.value === option.value;
        const isHighlighted = index === highlightedIndex;
        const optionKey = `${String(option.value)}-${index}`;

        return (
          <button
            id={getOptionId(index)}
            key={optionKey}
            ref={node => {
              selectedOptionRef?.(index, node);
            }}
            type="button"
            role="option"
            aria-selected={isSelected}
            aria-disabled={disabled || option.isDisabled}
            tabIndex={-1}
            disabled={disabled || option.isDisabled}
            onClick={() => onOptionClick(option)}
            onMouseMove={() => onOptionHover?.(index)}
            className={cn(
              'w-full rounded-2xl px-4 py-3 text-left text-base leading-6 text-[var(--chekin-airbnb-zinc)] transition-colors',
              isHighlighted && 'bg-[var(--chekin-airbnb-surface)]',
              isSelected &&
                'bg-[var(--chekin-airbnb-surface)] font-medium text-[var(--chekin-airbnb-ink-black)]',
              (disabled || option.isDisabled) && 'cursor-not-allowed opacity-40',
            )}
          >
            {String(option.label)}
          </button>
        );
      })}
    </div>
  );
}

export {AirbnbSelectDesktopMenu};
